/* global Promise */
import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

window.jawViewerData = {
    scene: null,
    camera: null,
    renderer: null,
    controls: null,
    group: null,
    models: [],
};

export const initJawViewer = (canvas, base_url, filesToLoad = null, loaderEl = null, mtlFiles = []) => {
    if (!canvas) return;

    let width = canvas.parentElement.clientWidth || 800;
    const height = 585;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x474747);

    const camera = new THREE.PerspectiveCamera(10, width / height, 0.1, 1000);
    camera.position.set(0, 0, 150);

    const renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = false;

    // Reset up vector on manual interaction to ensure natural orbiting behavior
    controls.addEventListener("start", () => {

        if (camera.up.y !== 1) {
            camera.up.set(0, 1, 0);
            controls.update();
        }
    });

    // Reduce ambient significantly
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3); // was 0.72
    scene.add(ambientLight);

    // Keep one strong key light from top-front
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.5);
    keyLight.position.set(5, 15, 10);
    scene.add(keyLight);

    // Weak fill from opposite side
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-10, 5, 5);
    scene.add(fillLight);

    // Subtle back rim light
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
    rimLight.position.set(0, 10, -15);
    scene.add(rimLight);

    // Remove hemiLight or reduce drastically
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x222222, 0.2); // was 0.7
    hemiLight.position.set(0, 50, 0);
    scene.add(hemiLight);

    const cameraLight = new THREE.DirectionalLight(0xffffff, 0.8);
    camera.add(cameraLight);
    cameraLight.position.set(0, 0, 1);
    scene.add(camera); // camera must be added to scene for this to work

    // Static bottom light for upper jaw — off by default
    const upperJawLight = new THREE.DirectionalLight(0xffffff, 0);
    upperJawLight.position.set(0, -30, 5); // dari bawah
    scene.add(upperJawLight);

    const objLoader = new OBJLoader();
    const mtlLoader = new MTLLoader();
    const group = new THREE.Group();
    scene.add(group);

    // const material = new THREE.MeshPhysicalMaterial({
    //     color: 0xdcdcdc,
    //     roughness: 0.65,      // more matte = more contrast
    //     metalness: 0,
    //     clearcoat: 0.1,       // reduce clearcoat, was too shiny
    //     clearcoatRoughness: 0.8,
    //     side: THREE.DoubleSide,
    //     emissive: new THREE.Color(0xffffff), // remove emissive
    //     emissiveIntensity: 0.08,
    // });

    // Make references global
    window.jawViewerData.scene = scene;
    window.jawViewerData.camera = camera;
    window.jawViewerData.renderer = renderer;
    window.jawViewerData.controls = controls;
    window.jawViewerData.group = group;
    window.jawViewerData.models = [];
    window.jawViewerData.upperJawLight = upperJawLight;

    const objFiles = filesToLoad || [];

    const buildMaterial = (mtlMat = null) => {
        const roughness = mtlMat?.shininess != null
            ? 1 - (mtlMat.shininess / 1000)
            : 0.65;

        // THREE.js Color object is always truthy even if RGB is 0,0,0 — check values directly
        const emissive = mtlMat?.emissive || new THREE.Color(0x000000);
        const emissiveIntensity = (emissive.r === 0 && emissive.g === 0 && emissive.b === 0) ? 0 : 1;

        return new THREE.MeshPhysicalMaterial({
            color: mtlMat?.color || new THREE.Color(0.8, 0.8, 0.8),
            emissive: emissive,
            emissiveIntensity: emissiveIntensity,
            roughness: roughness,
            specularColor: mtlMat?.specular || new THREE.Color(0.8, 0.8, 0.8),
            metalness: 0,
            clearcoat: 0.1,
            clearcoatRoughness: 0.8,
            side: THREE.DoubleSide,
        });
    };

    const loadMtl = (mtlUrl) => {
        return new Promise((resolve) => {
            if (!mtlUrl) return resolve(null);

            const url =
                mtlUrl.startsWith("http") || mtlUrl.startsWith("/")
                    ? mtlUrl
                    : `${base_url}/wp-content/themes/starter-theme-custom-doctor/assets/src/models/${mtlUrl}`;

            mtlLoader.load(
                url,
                (materials) => {
                    materials.preload();
                    const firstMat = Object.values(materials.materials)[0] || null;
                    resolve(firstMat);
                },
                undefined,
                () => {
                    console.warn("MTL failed to load:", url);
                    resolve(null);
                }
            );
        });
    };

    const loadObj = (file, material) => {
        return new Promise((resolve, reject) => {
            const url =
                file.startsWith("http") || file.startsWith("/")
                    ? file
                    : `${base_url}/wp-content/themes/starter-theme-custom-doctor/assets/src/models/${file}`;

            objLoader.load(
                url,
                (obj) => {
                    obj.traverse((child) => {
                        if (child.isMesh) {
                            child.material = material;
                        }
                    });
                    resolve(obj);
                },
                undefined,
                reject,
            );
        });
    };

    const loadAll = async () => {
        const mtlMaterials = await Promise.all(
            objFiles.map((_, index) => loadMtl(mtlFiles[index] || ""))
        );

        return Promise.all(
            objFiles.map((file, index) => {
                const material = buildMaterial(mtlMaterials[index]);
                return loadObj(file, material);
            })
        );
    };

    loadAll()
        .then((models) => {
            models.forEach((model, index) => {
                model.userData.fileName = objFiles[index];
                if (index !== 2) {
                    model.visible = false;
                }
                group.add(model);
                window.jawViewerData.models.push(model);
            });

            const box = new THREE.Box3().setFromObject(group);
            const center = box.getCenter(new THREE.Vector3());
            group.position.x -= center.x;
            group.position.y -= center.y;
            group.position.z -= center.z;

            const size = box.getSize(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);
            const fov = camera.fov * (Math.PI / 180);
            let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
            cameraZ *= 1.2;
            camera.position.z = cameraZ;
            camera.updateProjectionMatrix();

            if (loaderEl) {
                loaderEl.style.display = "none";
            }
        })
        .catch((err) => {
            console.error("Error loading OBJ files:", err);
            if (loaderEl) {
                loaderEl.innerHTML = "Error loading 3D Models";
            }
        });

    window.addEventListener("resize", () => {
        if (!canvas.isConnected) return;
        width = canvas.parentElement.clientWidth || 800;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
    });

    const animate = () => {
        if (!canvas.isConnected) return;
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
    };
    animate();
};
